import React, { useEffect, useRef } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TextAreaProps as KendoTextAreaProps, TextAreaChangeEvent } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import cn from 'clsx';
import styles from './styles.module.scss';
import { TextArea } from '@components-clean/core/text-area';
import { useTranslation, useBoolean } from '@hooks/index';
import { validateLimitExceeded } from '@utils/validation';

interface TextAreaDropdownProps extends KendoTextAreaProps {
    'data-testid': string;
    label?: string;
}

interface TextAreaDropdownControllerProps<TData, TControl extends FieldValues> extends TextAreaDropdownProps {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean | undefined>;
    handleInputChange?: (event: TextAreaChangeEvent) => void;
    expanded?: boolean;
}

const TextAreaDropdownController = <TData, TControl extends FieldValues>({
    control,
    name,
    disabled,
    'data-testid': dataTestId,
    required = false,
    label,
    maxLength,
    rows,
    fillMode,
    readOnly,
    validateRules,
    handleInputChange,
    expanded = false,
    ...props
}: TextAreaDropdownControllerProps<TData, TControl>) => {
    const { t } = useTranslation();
    const isDisplayComponent = useBoolean(expanded);
    const textAreaRef = useRef<HTMLDivElement | null>(null);
    const collapseOnBlur = useRef<boolean>(false);
    const scrollIntoView = useRef<boolean>(false);
    const hasChanged = useRef<boolean>(false);

    const scrollTextAreaIntoView = () => {
        textAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    useEffect(() => {
        if (!textAreaRef.current) return;
        const handleTransitionEnd = () => {
            // We only want to scroll into view when the user is tabbing to the component and it's currently collapsed
            if (scrollIntoView.current) {
                scrollTextAreaIntoView();
                // Now that it's scrolled, reset the flag
                scrollIntoView.current = false;
            }
        };

        textAreaRef.current.addEventListener('transitionend', handleTransitionEnd);

        return () => {
            textAreaRef.current?.removeEventListener('transitionend', handleTransitionEnd);
        };
    }, []);

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    maxLength: (value) => {
                        if (maxLength) {
                            return validateLimitExceeded(value as string, maxLength);
                        }
                        return true;
                    },
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value, name: fieldName }, fieldState: { error } }) => {
                return (
                    <div
                        className={cn(
                            styles.borderPadding,
                            styles.paddingTopTextAreaDropdown,
                            'k-d-flex k-flex-auto k-flex-col k-border-solid k-border-primary k-rounded-md k-pl-sm k-pr-sm k-pb-xs k-overflow-hidden',
                        )}
                    >
                        <div className="k-d-flex k-align-items-center">
                            <Label>{label}</Label>
                            <FontAwesomeIcon
                                aria-label="text-area-dropdown-button"
                                id="text-area-dropdown-button"
                                icon={['fal', isDisplayComponent.value ? 'chevron-up' : 'chevron-down']}
                                className={cn('fa-lg arrow-btn k-ml-auto')}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    // If collapseOnBlur is true, that means it's open and will collapse on blur.
                                    // Clicking on this arrow counts as a blur and so this will already be closing
                                    // Reset the flag, no need to toggle the value
                                    if (collapseOnBlur.current) collapseOnBlur.current = false;
                                    else isDisplayComponent.toggle();
                                }}
                            />
                        </div>
                        <div
                            ref={textAreaRef}
                            className={cn(
                                styles.accordionContent,
                                isDisplayComponent.value ? styles.accordionContentExpanded : 'k-overflow-hidden',
                            )}
                        >
                            <TextArea
                                {...props}
                                name={fieldName}
                                data-testid={dataTestId}
                                disabled={disabled}
                                valid={!error}
                                value={value as string}
                                validationMessage={error?.message}
                                onChange={(event) => {
                                    onChange(event.target.value);
                                    if (handleInputChange) handleInputChange(event);
                                }}
                                fillMode={fillMode}
                                readOnly={readOnly}
                                maxLength={maxLength}
                                rows={rows}
                                onFocus={() => {
                                    if (!isDisplayComponent.value) {
                                        isDisplayComponent.toggle();
                                        collapseOnBlur.current = true;
                                        scrollIntoView.current = true;
                                    } else {
                                        scrollTextAreaIntoView();
                                    }
                                }}
                                onBlur={() => {
                                    if (!value) {
                                        isDisplayComponent.setFalse();
                                    } else {
                                        collapseOnBlur.current = false;
                                    }

                                    scrollIntoView.current = false;
                                    hasChanged.current = false;
                                }}
                            />
                        </div>
                    </div>
                );
            }}
        />
    );
};

export { TextAreaDropdownController };
